/**
 * The install steps and the install-time claim. Every install surface on the
 * site renders from this module (ANTI_SLOP.md rule 4: one step count, order
 * and wording): the condensed three-step form on post-download and hub
 * surfaces, the expanded form of the same three steps on /guide and the SEO pages, and the
 * HowTo JSON-LD for whichever form a page shows.
 *
 * If you reword a step here, every consumer updates together. Do not add a
 * competing hand-written step list to a component;
 * lib/__tests__/install-surfaces.test.ts fails on one.
 */

import { TESLA_FACTS } from "@/lib/tesla-facts";

const { filename, path, folder, formats, portNote } = TESLA_FACTS.usb;
const { finalStep } = TESLA_FACTS.menu;
const { sampleRate, bitDepth } = TESLA_FACTS.audio.recommended;

/** The one install-time claim, in prose: "2 minutes". */
export const INSTALL_TIME_LABEL = TESLA_FACTS.install.label;
/** The same claim where space is short (the nav CTA): "2 min". */
export const INSTALL_TIME_SHORT = `${TESLA_FACTS.install.minutes} min`;
/** ISO-8601 duration for HowTo structured data. */
export const INSTALL_TIME_ISO = TESLA_FACTS.install.iso;

export interface CondensedInstallStep {
  /** Short imperative title (wizard rows, headings). */
  title: string;
  /** Full one-sentence instruction (receipt lines, toast list items). */
  body: string;
}

export interface ExpandedInstallStep {
  /** Short imperative title (card headings). */
  title: string;
  /** Longer instruction for SEO-page install sections. */
  description: string;
}

/**
 * The install path, once: three steps, one title each. The condensed and
 * expanded forms are both read off this list, so they cannot disagree on count,
 * order or headings; only the amount of explanation differs.
 */
const INSTALL_PATH = [
  {
    title: "Copy to USB",
    body: `Copy ${filename} onto a FAT32 or exFAT USB drive at ${path}.`,
    description: `Download your sound. Every file arrives named ${filename} in ${sampleRate}, ${bitDepth} WAV, the recommended spec for clean playback. On a ${formats[0]} or ${formats[1]} USB drive, create a folder named ${folder} and place ${filename} inside it. Full path: ${path}. The only hard Tesla requirement is a WAV file named ${filename}.`,
  },
  {
    title: "Plug into your Tesla",
    body: `Plug the drive into a USB port that carries data (the glovebox port on newer cars). Open ${finalStep}.`,
    description: `Insert the drive into ${portNote}. Give the car a few seconds to index the drive. Open ${finalStep}.`,
  },
  {
    title: "Lock the car",
    body: "Lock the car to hear it.",
    description: "Lock the car to hear it.",
  },
] as const;

/**
 * What a browser names a second download of the same file when the first is
 * still in the downloads folder ("LockChime (1).wav"). The car reads only the
 * exact filename, so post-download surfaces warn about it. Built from the
 * facts module's filename, not typed.
 */
export const DUPLICATE_DOWNLOAD_FILENAME = filename.replace(/(\.[^.]+)$/, " (1)$1");

/** The one-line warning post-download surfaces show under the steps. */
export const DUPLICATE_DOWNLOAD_NOTE = `Saved as ${DUPLICATE_DOWNLOAD_FILENAME} or similar? Rename it to ${filename} before you copy it. The car only reads that exact name.`;

/** How many steps the install path has. Every "N steps" on the site reads this. */
export const INSTALL_STEP_COUNT = INSTALL_PATH.length;

/** The same count as a lower-case word ("three"), for prose and metadata. */
export const INSTALL_STEP_COUNT_WORD = (["one", "two", "three", "four", "five", "six"] as const)[INSTALL_STEP_COUNT - 1];

export const CONDENSED_INSTALL_STEPS: readonly CondensedInstallStep[] = INSTALL_PATH.map(({ title, body }) => ({ title, body }));

/**
 * Canonical EXPANDED install steps for /guide and the SEO pages (horn-sounds,
 * lockchime-wav, tesla-unlock-sound, boombox-sounds). Same three steps, longer
 * form. Do not add a competing hand-written step list to a page.
 */
export const EXPANDED_INSTALL_STEPS: readonly ExpandedInstallStep[] = INSTALL_PATH.map(({ title, description }) => ({
  title,
  description,
}));

export type InstallStepsVariant = "condensed" | "expanded";

export interface InstallStep {
  title: string;
  detail: string;
}

/** Both forms in one shape, for the InstallSteps component and HowTo JSON-LD. */
export const INSTALL_STEPS: Record<InstallStepsVariant, readonly InstallStep[]> = {
  condensed: CONDENSED_INSTALL_STEPS.map((step) => ({ title: step.title, detail: step.body })),
  expanded: EXPANDED_INSTALL_STEPS.map((step) => ({ title: step.title, detail: step.description })),
};

/** HowTo `step` entries matching what `<InstallSteps variant={variant} />` shows. */
export function installHowToSteps(variant: InstallStepsVariant) {
  return INSTALL_STEPS[variant].map((step, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: step.title,
    text: step.detail,
  }));
}
